// Phase 2 commands: the counter and the money that moves there (04 §4.2, §4.6).
import { z } from 'zod';
import { defineCommand } from '../define';
import {
  SERVICES,
  agentIdSchema,
  numberSeriesIdSchema,
  serviceLineSchema,
  sizeCodeSchema,
  slugSchema,
} from '../config';
import {
  addressSchema,
  barcodeSchema,
  consigneeIdSchema,
  customerIdSchema,
  dimsCmSchema,
  e164Schema,
  packingItemSchema,
  receiptNoSchema,
  trackingNoSchema,
} from '../cargo';
import { vatInputSchema } from '../finance';
import { MONEY_OUT_CATEGORIES, PAYMENT_METHODS, moneyLinkSchema } from '../money';

const minor = z.number().int().nonnegative().max(1e12);

/** A voucher code: capitals and digits, 8 to 12 (the codes `redeemVoucher` and `resolveTicket` make). */
export const voucherCodeSchema = z
  .string()
  .regex(/^[A-Z0-9]{8,12}$/, 'Voucher code like WELCOME10');

export const paymentInSchema = z
  .object({
    method: z.enum(PAYMENT_METHODS),
    amountMinor: minor.refine((n) => n > 0, 'Amount must be more than zero'),
    reference: z.string().max(80).default(''),
  })
  .strict();

/** The shipper: an existing customer, or a new one created in the same transaction. */
export const shipperRefSchema = z.union([
  z.object({ customerId: customerIdSchema }).strict(),
  z
    .object({
      newCustomer: z
        .object({
          name: z.string().trim().min(1).max(120),
          phoneE164: e164Schema,
          email: z.string().max(120).default(''),
          source: z
            .enum(['walk_in', 'referral', 'agent', 'website', 'app', 'social', 'other'])
            .default('walk_in'),
        })
        .strict(),
    })
    .strict(),
]);

export const consigneeInputSchema = z
  .object({
    consigneeId: consigneeIdSchema.optional(),
    name: z.string().trim().min(1).max(120),
    phones: z.array(e164Schema).min(1).max(3),
    address: addressSchema,
    cityId: slugSchema,
    relationship: z.string().max(40).default(''),
    saveToAddressBook: z.boolean().default(true),
  })
  .strict();

export const collectBoxSchema = z
  .object({
    barcode: barcodeSchema,
    sizeId: sizeCodeSchema,
    weightKg: z.number().positive().max(500).optional(),
    dimsCm: dimsCmSchema.optional(),
    /** The empty box was sold now with this shipment (stock decremented, box revenue on the invoice). */
    boxSoldNow: z.boolean().default(false),
  })
  .strict();

/**
 * A Prime piece (12 §12.5): a document or parcel weighed at the counter. The server makes its label
 * `{trackingNo}-P{n}` and prices Prime by weight from the rate card.
 */
export const collectPieceSchema = z
  .object({
    weightKg: z.number().positive().max(500),
    dimsCm: dimsCmSchema.optional(),
    description: z.string().trim().min(1).max(120),
  })
  .strict();

/**
 * Creates the shipment at the counter (04 §4.2). The server re-prices; `expectedGrossMinor` is
 * what the device showed, and a difference opens a `price_mismatch` exception.
 */
export const collectCargo = defineCommand({
  type: 'collectCargo',
  permission: 'cargo.collect',
  principals: [],
  offline: true,
  payload: z
    .object({
      trackingNo: trackingNoSchema,
      /**
       * `typed`: staff typed the number instead of the generated one (D-27). It needs
       * `cargo.tracking.custom`, is not checked against a lease, and may not be a number any active
       * series could generate. Absent or `generated`: a number from this device's lease.
       */
      trackingSource: z.enum(['generated', 'typed']).optional(),
      /** The series the generated number was taken from (the branch code for the branch default). */
      seriesId: numberSeriesIdSchema.optional(),
      /** The agent who brought the shipment (`agents/{id}`), when one was chosen. */
      agentId: agentIdSchema.optional(),
      shipper: shipperRefSchema,
      consignee: consigneeInputSchema,
      service: z.enum(SERVICES),
      /** The product the customer bought (D-28). `service` stays the handling mode. */
      serviceLine: serviceLineSchema.optional(),
      /** Balikbayan boxes with pre-printed barcodes. A Prime shipment may carry `pieces` instead. */
      boxes: z.array(collectBoxSchema).max(200).default([]),
      /** Prime pieces (12 §12.5): the server creates their labels `{trackingNo}-P{n}`. */
      pieces: z.array(collectPieceSchema).max(50).default([]),
      packingList: z.array(packingItemSchema).max(200).default([]),
      declaredValueMinor: minor.default(0),
      addOnCodes: z
        .array(z.string().regex(/^[a-z_]{2,30}$/))
        .max(20)
        .default([]),
      discountMinor: minor.default(0),
      seasonTags: z.array(slugSchema).max(5).default([]),
      payments: z.array(paymentInSchema).max(5).default([]),
      receiptNo: receiptNoSchema.optional(),
      /**
       * A voucher the customer brought (15 §15.2). The server checks it (the shipper's, not expired,
       * unused, minimum spend) and takes its value as a payment of method `voucher` (Dr 2020, Cr 1100)
       * under the receipt, so the cash lines only have to cover the rest. `expectedGrossMinor` may be the
       * price before or after the voucher.
       */
      voucherCode: voucherCodeSchema.optional(),
      expectedGrossMinor: minor,
      /** The shipper's signature, uploaded by this device: must be `uploads/{actor uid}/…` (07 §7.4). */
      signaturePath: z.string().max(200).optional(),
    })
    .strict()
    .refine((p) => p.boxes.length > 0 !== p.pieces.length > 0, {
      message: 'Send boxes, or Prime pieces, but not both and not neither',
      path: ['boxes'],
    })
    .refine((p) => p.pieces.length === 0 || p.service === 'prime', {
      message: 'Pieces are for Prime shipments only',
      path: ['pieces'],
    }),
});

export const recordPayment = defineCommand({
  type: 'recordPayment',
  permission: 'finance.payment.record',
  principals: [],
  offline: true,
  payload: z
    .object({
      shipmentId: trackingNoSchema,
      /** The money taken. Optional when a `voucherCode` pays all (or the rest is paid another time). */
      payment: paymentInSchema.optional(),
      /** A voucher the customer brought: its value is applied first, as a payment of method `voucher`. */
      voucherCode: voucherCodeSchema.optional(),
      receiptNo: receiptNoSchema,
      /** Pay more than the balance and keep the rest as customer credit (06 §6.3). */
      toDeposit: z.boolean().default(false),
    })
    .strict()
    .refine((p) => p.payment !== undefined || p.voucherCode !== undefined, {
      message: 'Send a payment, a voucher code, or both',
      path: ['payment'],
    }),
});

export const sellEmptyBox = defineCommand({
  type: 'sellEmptyBox',
  permission: 'cargo.collect',
  principals: [],
  offline: true,
  payload: z
    .object({
      customer: shipperRefSchema.optional(),
      items: z
        .array(z.object({ sizeId: sizeCodeSchema, qty: z.number().int().min(1).max(500) }).strict())
        .min(1)
        .max(20),
      barcodes: z.array(barcodeSchema).max(500).default([]),
      payment: paymentInSchema.optional(),
      receiptNo: receiptNoSchema.optional(),
    })
    .strict(),
});

export const receiveEmptyBoxes = defineCommand({
  type: 'receiveEmptyBoxes',
  permission: 'stock.manage',
  principals: [],
  offline: true,
  payload: z
    .object({
      supplierName: z.string().trim().min(1).max(120),
      items: z
        .array(
          z
            .object({
              sizeId: sizeCodeSchema,
              qty: z.number().int().min(1).max(10000),
              unitCostMinor: minor,
            })
            .strict(),
        )
        .min(1)
        .max(20),
      paidNow: z.boolean().default(false),
      method: z.enum(PAYMENT_METHODS).optional(),
      /** The supplier's VAT inside the total (06 §6.3): posted to 1040 VAT input, apart from the stock. */
      vat: vatInputSchema.optional(),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

export const countEmptyBoxes = defineCommand({
  type: 'countEmptyBoxes',
  permission: 'stock.manage',
  principals: [],
  offline: true,
  payload: z
    .object({
      items: z
        .array(
          z
            .object({ sizeId: sizeCodeSchema, counted: z.number().int().min(0).max(100000) })
            .strict(),
        )
        .min(1)
        .max(20),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

export const openTill = defineCommand({
  type: 'openTill',
  permission: 'finance.till',
  principals: [],
  offline: true,
  payload: z.object({ openingFloatMinor: minor }).strict(),
});

export const closeTill = defineCommand({
  type: 'closeTill',
  permission: 'finance.till',
  principals: [],
  offline: true,
  payload: z
    .object({
      tillId: z.string().min(1).max(40),
      countedMinor: minor,
      note: z.string().max(300).default(''),
    })
    .strict(),
});

export const closeDay = defineCommand({
  type: 'closeDay',
  permission: 'finance.day.close',
  principals: [],
  offline: false,
  payload: z
    .object({
      date: z.iso.date(),
      countedMinor: minor,
      /** Required to close while a device of the branch still has unsynced commands (04 §4.6). */
      overrideReason: z.string().max(300).optional(),
    })
    .strict(),
});

export const recordMoneyOut = defineCommand({
  type: 'recordMoneyOut',
  permission: 'finance.expense.record',
  principals: [],
  offline: true,
  payload: z
    .object({
      walletId: z.string().min(1).max(40),
      category: z.enum(MONEY_OUT_CATEGORIES),
      amountMinor: minor.refine((n) => n > 0, 'Amount must be more than zero'),
      payee: z.string().trim().min(1).max(120),
      link: moneyLinkSchema.optional(),
      /** The supplier's VAT inside the amount paid (06 §6.3): posted to 1040 VAT input, apart from the cost. */
      vat: vatInputSchema.optional(),
      note: z.string().max(300).default(''),
      receiptPath: z.string().max(200).optional(),
    })
    .strict(),
});

export const moveMoney = defineCommand({
  type: 'moveMoney',
  permission: 'finance.bank.manage',
  principals: [],
  offline: true,
  payload: z
    .object({
      fromWalletId: z.string().min(1).max(40),
      toWalletId: z.string().min(1).max(40),
      amountMinor: minor.refine((n) => n > 0, 'Amount must be more than zero'),
      /** Optional: what the move belongs to (14 §14.3), e.g. cash taken to a container's port fees. */
      link: moneyLinkSchema.optional(),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

export const decideApproval = defineCommand({
  type: 'decideApproval',
  permission: 'finance.approve',
  principals: [],
  offline: false,
  payload: z
    .object({
      approvalId: z.string().min(1).max(40),
      decision: z.enum(['approve', 'reject']),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

/**
 * The receipt of money already paid out (`recordMoneyOut`, by its command id) or of a recorded bill,
 * attached later (D-32): the `receipt_missing` exception it left closes.
 */
export const attachReceipt = defineCommand({
  type: 'attachReceipt',
  permission: 'finance.expense.record',
  alsoPermitted: ['finance.bill.record'],
  principals: [],
  offline: false,
  payload: z
    .object({
      target: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('moneyOut'), commandId: z.string().min(1).max(60) }).strict(),
        z.object({ kind: z.literal('bill'), billId: z.string().min(1).max(40) }).strict(),
      ]),
      receiptPath: z.string().min(1).max(200),
      note: z.string().max(300).default(''),
    })
    .strict(),
});

export const COUNTER_COMMANDS = {
  collectCargo,
  recordPayment,
  sellEmptyBox,
  receiveEmptyBoxes,
  countEmptyBoxes,
  openTill,
  closeTill,
  closeDay,
  recordMoneyOut,
  attachReceipt,
  moveMoney,
  decideApproval,
} as const;
